import { useCallback, useEffect, useMemo, useState } from 'react';
import { getId } from '@shared/helpers/object.helpers';
import {
  applyCategoryPickerCategoryId,
  applyCategoryPickerPropertyChange,
  buildCategoryPickerElement,
  createCategoryPickerStateFromDefaults,
  getCategoryPickerVisibleProperties,
  getGenericProductPickerChange,
  type CategoryPickerState,
  type GenericProductPickerChange,
} from '@shared/helpers/generic-product-picker.helpers';
import type { ElementCategoryID } from '@shared/models/element-categories.model';
import type { PropertyChange } from '@shared/helpers/element-property.helpers';
import type { IProduct } from '@shared/models/product.model';
import type { IElement } from '@shared/models/project.model';

type UseCategoryPropertyPickerOptions = {
  genericProductId?: string;
  defaults?: IElement | IProduct;
  excludePropertyNames: readonly string[];
};

/**
 * Local EPD category + property state backed by category conditional nodes.
 */
export const useCategoryPropertyPicker = ({
  genericProductId,
  defaults,
  excludePropertyNames,
}: UseCategoryPropertyPickerOptions) => {
  const [state, setState] = useState<CategoryPickerState>(() =>
    createCategoryPickerStateFromDefaults(defaults, genericProductId),
  );

  const defaultsSyncKey = defaults
    ? `${getId(defaults)}:${defaults.category_id ?? ''}`
    : '';

  // Re-initialize when editing a different product/element or its category defaults
  useEffect(() => {
    setState(createCategoryPickerStateFromDefaults(defaults, genericProductId));
  }, [defaultsSyncKey, defaults, genericProductId]);

  const pickerElement = useMemo(
    () =>
      state.category_id
        ? buildCategoryPickerElement(
            state.category_id,
            state.category_property_value_record,
          )
        : undefined,
    [state.category_id, state.category_property_value_record],
  );

  const visibleProperties = useMemo(() => {
    if (!pickerElement) return [];
    return getCategoryPickerVisibleProperties(
      pickerElement,
      excludePropertyNames,
    );
  }, [excludePropertyNames, pickerElement]);

  const setCategoryId = useCallback((category_id: ElementCategoryID) => {
    setState((prev) =>
      applyCategoryPickerCategoryId(
        category_id,
        prev.category_property_value_record,
      ),
    );
  }, []);

  const setPropertyChange = useCallback((change: PropertyChange) => {
    setState((prev) => {
      if (!prev.category_id) return prev;
      return {
        ...prev,
        category_property_value_record: applyCategoryPickerPropertyChange(
          prev.category_id,
          prev.category_property_value_record,
          change,
        ),
      };
    });
  }, []);

  const change: GenericProductPickerChange = useMemo(
    () => getGenericProductPickerChange(state, genericProductId),
    [genericProductId, state],
  );

  return {
    state,
    visibleProperties,
    setCategoryId,
    setPropertyChange,
    change,
  };
};
